import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import TimeEntryService from '../../services/time-entry.service';

const TimeEntryList: React.FC = () => {
  const navigate = useNavigate();
  const [timeEntries, setTimeEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTimeEntries();
  }, []);

  const fetchTimeEntries = async () => {
    try {
      setLoading(true);
      const response = await TimeEntryService.getAll({
        populate: '*',
        'pagination[pageSize]': 100,
      });
      setTimeEntries(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching time entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this time entry?')) {
      try {
        await TimeEntryService.delete(id);
        fetchTimeEntries();
      } catch (error) {
        console.error('Error deleting time entry:', error);
      }
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Time Entries</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/time-entries/create')}
        >
          New Time Entry
        </Button>
      </Stack>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Employee</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Task</TableCell>
                <TableCell>Start Time</TableCell>
                <TableCell>End Time</TableCell>
                <TableCell align="right">Hours</TableCell>
                <TableCell>Billable</TableCell>
                <TableCell align="right">Cost</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {timeEntries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>
                    {entry.attributes?.date ? new Date(entry.attributes.date).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    {entry.attributes?.employee?.data
                      ? `${entry.attributes.employee.data.attributes.firstName} ${entry.attributes.employee.data.attributes.lastName}`
                      : '-'}
                  </TableCell>
                  <TableCell>{entry.attributes?.project?.data?.attributes?.projectName || '-'}</TableCell>
                  <TableCell>{entry.attributes?.task?.data?.attributes?.title || '-'}</TableCell>
                  <TableCell>{entry.attributes?.startTime || '-'}</TableCell>
                  <TableCell>{entry.attributes?.endTime || '-'}</TableCell>
                  <TableCell align="right">{entry.attributes?.hours}</TableCell>
                  <TableCell>
                    <Chip
                      label={entry.attributes?.billable ? 'Yes' : 'No'}
                      size="small"
                      color={entry.attributes?.billable ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {entry.attributes?.totalCost ? `$${entry.attributes.totalCost.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/time-entries/${entry.id}`)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(entry.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && timeEntries.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No time entries found
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default TimeEntryList;

